import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export function LoginPage() {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.username, form.password);
      navigate('/today');
    } catch (err) {
      const msg = err.response?.data?.error || 'Login failed';
      if (err.response?.status === 403) {
        setNeedsVerify(err.response.data.email || form.username);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (needsVerify) return <VerifyEmailPage email={needsVerify} onBack={() => setNeedsVerify(null)} />;

  return (
    <div style={authWrap}>
      <div style={authCard}>
        <div style={logoWrap}>
          <div style={logoIcon}>🏋️</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 32, letterSpacing: '0.05em' }}>
            MY IRON <span style={{ color: 'var(--red)' }}>JOURNAL</span>
          </h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>Track your daily wellness</p>
        </div>

        {!showForgot ? (
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="input-group">
              <label className="input-label">Username or Email</label>
              <input className="input" value={form.username}
                onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                placeholder="Username or email" autoComplete="username" />
            </div>
            <div className="input-group">
              <label className="input-label">Password</label>
              <input className="input" type="password" value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                placeholder="••••••••" autoComplete="current-password" />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button type="button" onClick={() => setShowForgot(true)}
                style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 13 }}>
                Forgot password?
              </button>
            </div>
            <button className="btn btn-primary btn-full btn-lg" disabled={loading}>
              {loading ? '⏳ Signing in...' : 'Sign In'}
            </button>
            <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--text-2)' }}>
              No account? <Link to="/register">Create one</Link>
            </p>
          </form>
        ) : (
          <ForgotPasswordForm onBack={() => setShowForgot(false)} />
        )}
      </div>
    <p style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.25)", marginTop: 20 }}>© 2026 StormForge Labs LLC</p>
    </div>
  );
}

function ForgotPasswordForm({ onBack }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const sendCode = async () => {
    setLoading(true);
    try {
      const { authAPI } = await import('../utils/api');
      await authAPI.forgotPassword(email);
      setStep('code');
      toast.success('Reset code sent to your email!');
    } catch { toast.error('Failed to send reset code'); }
    finally { setLoading(false); }
  };

  const resetPassword = async () => {
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (password.length < 8) { toast.error('Min 8 characters'); return; }
    setLoading(true);
    try {
      const { authAPI } = await import('../utils/api');
      await authAPI.resetPassword(email, code, password);
      toast.success('Password reset! Sign in with your new password.');
      onBack();
    } catch (err) { toast.error(err.response?.data?.error || 'Reset failed'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h3 style={{ fontWeight: 600 }}>Reset Password</h3>
      {step === 'email' ? (
        <>
          <div className="input-group">
            <label className="input-label">Your Email</label>
            <input className="input" type="email" value={email}
              onChange={e => setEmail(e.target.value)} placeholder="you@email.com" />
          </div>
          <button className="btn btn-primary btn-full" onClick={sendCode} disabled={!email || loading}>
            {loading ? 'Sending...' : 'Send Reset Code'}
          </button>
        </>
      ) : (
        <>
          <div className="input-group">
            <label className="input-label">6-Digit Code</label>
            <input className="input" value={code} onChange={e => setCode(e.target.value)}
              placeholder="123456" maxLength={6} style={{ fontSize: 24, letterSpacing: 8, textAlign: 'center' }} />
          </div>
          <div className="input-group">
            <label className="input-label">New Password</label>
            <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Confirm Password</label>
            <input className="input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-full" onClick={resetPassword}
            disabled={code.length !== 6 || !password || loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </>
      )}
      <button onClick={onBack} className="btn btn-ghost btn-sm">← Back to Sign In</button>
    </div>
  );
}

function VerifyEmailPage({ email, onBack }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const verify = async () => {
    setLoading(true);
    try {
      const { authAPI } = await import('../utils/api');
      const res = await authAPI.verifyEmail(email, code);
      localStorage.setItem('mij_token', res.data.token);
      localStorage.setItem('mij_refresh', res.data.refreshToken);
      localStorage.setItem('mij_user', JSON.stringify(res.data.user));
      navigate('/today');
    } catch (err) { toast.error(err.response?.data?.error || 'Invalid code'); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      const { authAPI } = await import('../utils/api');
      await authAPI.resendVerification(email);
      toast.success('Code resent!');
    } catch { toast.error('Failed to resend'); }
  };

  return (
    <div style={authWrap}>
      <div style={authCard}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
          <h2 style={{ fontWeight: 700, marginBottom: 8 }}>Check Your Email</h2>
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>
            We sent a 6-digit code to <strong>{email}</strong>
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <input className="input" value={code} onChange={e => setCode(e.target.value)}
            placeholder="000000" maxLength={6}
            style={{ fontSize: 32, letterSpacing: 12, textAlign: 'center', padding: '16px' }} />
          <button className="btn btn-primary btn-full btn-lg" onClick={verify}
            disabled={code.length !== 6 || loading}>
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button onClick={resend} className="btn btn-ghost btn-sm">Resend Code</button>
            <button onClick={onBack} className="btn btn-ghost btn-sm">← Back</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const [form, setForm] = useState({
    username: '', email: '', password: '', confirmPassword: '',
    height: '', weight: '', gender: '', measurement_scale: 'imperial',
    age_confirmed: false
  });
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const { register } = useAuth();

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { toast.error("Passwords don't match"); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!form.age_confirmed) { toast.error('You must confirm your age'); return; }
    setLoading(true);
    try {
      await register({
        username: form.username, email: form.email, password: form.password,
        height: form.height ? parseFloat(form.height) : undefined,
        weight: form.weight ? parseFloat(form.weight) : undefined,
        gender: form.gender || undefined,
        measurement_scale: form.measurement_scale,
        age_confirmed: form.age_confirmed
      });
      setRegistered(form.email);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
    } finally { setLoading(false); }
  };

  if (registered) return <VerifyEmailPage email={registered} onBack={() => setRegistered(false)} />;

  return (
    <div style={authWrap}>
      <div style={{ ...authCard, maxWidth: 520 }}>
        <div style={logoWrap}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, letterSpacing: '0.05em' }}>
            CREATE ACCOUNT
          </h2>
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>Join My Iron Journal</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="input-row">
            <div className="input-group">
              <label className="input-label">Username *</label>
              <input className="input" value={form.username} onChange={set('username')} placeholder="ironman42" />
            </div>
            <div className="input-group">
              <label className="input-label">Email *</label>
              <input className="input" type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" />
            </div>
          </div>
          <div className="input-row">
            <div className="input-group">
              <label className="input-label">Password *</label>
              <input className="input" type="password" value={form.password} onChange={set('password')} placeholder="Min 8 chars" />
            </div>
            <div className="input-group">
              <label className="input-label">Confirm Password *</label>
              <input className="input" type="password" value={form.confirmPassword} onChange={set('confirmPassword')} placeholder="••••••••" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {['imperial', 'metric'].map(s => (
              <button key={s} type="button"
                onClick={() => setForm(p => ({ ...p, measurement_scale: s }))}
                className={`btn btn-sm ${form.measurement_scale === s ? 'btn-primary' : 'btn-secondary'}`}
                style={{ flex: 1 }}>
                {s === 'imperial' ? 'Imperial (lbs/in)' : 'Metric (kg/cm)'}
              </button>
            ))}
          </div>

          <div className="input-row-3">
            <div className="input-group">
              <label className="input-label">{form.measurement_scale === 'imperial' ? 'Height (in)' : 'Height (cm)'}</label>
              <input className="input" type="number" value={form.height} onChange={set('height')} placeholder="70" />
            </div>
            <div className="input-group">
              <label className="input-label">{form.measurement_scale === 'imperial' ? 'Weight (lbs)' : 'Weight (kg)'}</label>
              <input className="input" type="number" value={form.weight} onChange={set('weight')} placeholder="180" />
            </div>
            <div className="input-group">
              <label className="input-label">Gender</label>
              <select className="input" value={form.gender} onChange={set('gender')}>
                <option value="">Select</option>
                {['Male','Female','Non-binary','Prefer not to say'].map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" checked={form.age_confirmed} onChange={set('age_confirmed')} />
            I confirm I am 13 years of age or older
          </label>

          <button className="btn btn-primary btn-full btn-lg" disabled={loading || !form.age_confirmed}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
          <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--text-2)' }}>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
    <p style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.25)", marginTop: 20 }}>© 2026 StormForge Labs LLC</p>
    </div>
  );
}

const authWrap = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--bg)', padding: 20
};
const authCard = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)', padding: 36, width: '100%', maxWidth: 440,
  boxShadow: 'var(--shadow)'
};
const logoWrap = { textAlign: 'center', marginBottom: 28 };
const logoIcon = { fontSize: 48, marginBottom: 12 };
